import React, { useState, Fragment } from 'react';
import numeral from 'numeral';
import axios from 'axios';
import {
  Button,
  CircularProgress,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faCircleQuestion, faPencil, faChartLine, faFloppyDisk, faTimes } from '@fortawesome/pro-light-svg-icons';
import styles from './IndicatorItem.module.scss';
import styled from 'styled-components';

const Dropdown = ({ options, value, onChange }) => {
  return (
    <Sel
      labelId="demo-simple-select-label"
      id="demo-simple-select"
      value={value}
      onChange={onChange}
    >
      {options.map(o => (<MenuItem value={o.value}>{o.label}</MenuItem>))}
    </Sel>
   );
};

const IndicatorItem = ({
  id,
  indicatorName,
  indicatorForecast,
  indicatorMeasured,
  indicatorMeans,
  indicatorTracking,
  handleSaveOrUpdate,
  resultId,
  beneficiary,
  classificationOpts,
}) => {
  const [ editable, setEditable ] = useState(false);
  const [ valueForecast, setValueForecast ] = useState(indicatorForecast);
  const [ valueMeans, setValueMeans ] = useState(indicatorMeans);
  const [ valueMeasured, setValueMeasured ] = useState(indicatorMeasured);
  const [ valueFile, setValueFile ] = useState(indicatorTracking);
  const [ uploading, setUploading ] = useState(false);
  const [ invalidFile, setInvalidFile ] = useState(false);

  const handleEdit = () => {
    if (editable) {
      handleSaveOrUpdate({
        forecastCurrent: valueForecast,
        classification: valueMeasured,
        description: valueMeans,
        indicatorId: id,
        id: resultId
      });
    }
    setEditable(!editable);
  };

  const handleChangeForecast = (ev) => {
    setValueForecast(ev.target.value);
  };

  const handleChangeMeans = (ev) => {
    setValueMeans(ev.target.value);
  };

  const handleChangeMeasured = (ev) => {
    setValueMeasured(ev.target.value);
  };

  const handleChangeFile = async (ev) => {
    setInvalidFile(false);
    setUploading(true);
    const file = ev.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = `/v4/results/${resultId}/result_files`;
    try {
      const response = await axios({
        method: 'post',
        url: endpoint,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { attributes } = response?.data?.data;
      setValueFile(`${attributes?.name}`);
    } catch(error) {
      setInvalidFile('Invalid file. Accepted file formats: DOC, DOCX, ODT, PDF, XLS, XLSX, ODS, PPT, PPTX, TXT.');
    }
    setUploading(false);
  };

  const handleClearFile = async () => {
    setValueFile();
    setInvalidFile(false);
    setUploading(false);
    try {
      const endpoint = `/v4/results/${resultId}/result_files`;
      await axios({
        method: 'delete',
        url: endpoint,
      });
    } catch(error) {
      console.warn(error);
    }
  };

  const indicatorLabel = classificationOpts.filter(opt => opt.value === indicatorMeasured)?.[0]?.label;

  return (
    <div className={styles.indicator__container}>
      <div className={styles.indicator__item}>
        <span className={`heading-label ${styles.heading__label}`}>Indicator</span>
        <h4 className={`heading ${styles.heading}`}>
          {indicatorName} <FontAwesomeIcon icon={faCircleQuestion} />
        </h4>
        <div className={`body-base ${styles.indicator__details}`}>
          <div className={`${styles.forecast__container} ${editable ? 'editable' : ''}`}>
            {!editable ? // forecast
              (<span className={`display-number ${styles.forecast__item}`}>{numeral(indicatorForecast).format(0, 0)}</span>) :
              <Input type="number" value={valueForecast} onChange={handleChangeForecast} />
            }
            <span className={styles.forecast__explainer}>{beneficiary} {indicatorName}</span>
            <span className={`heading-label ${styles.heading__label}`}><FontAwesomeIcon icon={faChartLine}/> Forecast</span>
          </div>
          <div className={styles.details__container}>
            <div className={styles.details__item}>
              <h5 className={`heading-subheading ${styles.heading__subheading}`}>How it is measured:</h5>
              <div className={`body-base ${styles.editable_content} ${editable ? 'editable' : ''} ` +
                `${indicatorMeasured ? 'active' : 'empty'}`}>
              {!editable ? // measured
                <span>{indicatorLabel || 'Not Yet Entered' } </span> :
                <Dropdown options={classificationOpts} value={valueMeasured} onChange={handleChangeMeasured}/>
              }
              </div>
            </div>
            <div className={styles.details__item}>
              <h5 className={`heading-subheading ${styles.heading__subheading}`}>What it means:</h5>
              <div className={`body-base ${styles.editable_content_big} ${editable ? 'editable' : ''} ` +
                `${indicatorMeans ? 'active' : 'empty'}`}>
                {!editable ? // means
                  <span>{indicatorMeans || 'Not Yet Entered' }</span> :
                  <TextArea
                    value={valueMeans}
                    onChange={handleChangeMeans}
                    multiline
                    rows={4}
                  />
                }
              </div>
            </div>
          </div>
          <div className={styles.edit__container} onClick={handleEdit}>
            <div className={`cta-icon__container base ${editable ? 'active' : ''}`}>
              { editable ? <FontAwesomeIcon icon={faFloppyDisk} /> : <FontAwesomeIcon icon={faPencil} /> }
            </div>
          </div>
        </div>
        <div className={styles.attachment__container}>
          { valueFile ? 
            <span className="body-base">
            <span className={styles.attachment__icon}>
              <FontAwesomeIcon icon={faFile} /> 
            </span>
            {valueFile}
            <span className={styles.remove} onClick={handleClearFile}>
              <FontAwesomeIcon icon={faTimes}/>
            </span>
          </span>
          :
          (editable && <Fragment>
            <Button onClick={() => { document.getElementById(`input_file_${id}`).click(); }}>Add tracking tool</Button>
            {invalidFile&&<FileError> {invalidFile}</FileError>}
            {!invalidFile&&!valueFile&&<FileText> Accepted file formats: DOC, PDF, XLSX</FileText>}
            {!invalidFile&&valueFile&&(<FileSelected> File Selected: {valueFile}</FileSelected>)}
            { uploading && <CircularProgress size="30px"/> }
            <input style={{display: 'none'}} id={`input_file_${id}`} type="file" onChange={handleChangeFile}/>
          </Fragment>)
          }
        </div>
      </div>
    </div>
  );
};

export default IndicatorItem;

const Input = styled.input`
  border: none;
  width: 100%;
  height: 35px;
  border-radius: 4px;
  font-size: 17px;
  padding-left: 15px;
`;

const TextArea = styled(TextField)`
  border: none;
  background-color: white;
  width: 100%;
  height: 35px;
  border-radius: 4px;
  font-size: 17px;
  padding-left: 15px;
  div {
    background-color: white;
  }
  textarea {
    background-color: white;
  }
`;

const Sel = styled(Select)`
  width: 100%;
  max-width: 450px;
  background-color: #ffffff;
`;
const FileText = styled.span`
  font-style: italic;
  font-size: 14px;
  font-weight: 400;
`;
const FileSelected = styled.span`
  color: #0072A2;
  font-style: italic;
  font-size: 14px;
  font-weight: 700;
`;
const FileError = styled.span`
  color: #f34747;
  font-style: italic;
  font-size: 14px;
  font-weight: 700;
`;
